/**
 * The Day view: an hour grid with each entry as a block, a thin activity
 * ribbon on the left (agent runs dashed; context only), and labels for gaps
 * with no activity. Clicking empty time adds an entry for a call or offline
 * work; the side panel shows the selected entry, or the day by case.
 */
import { Check, Info, Plus } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MouseEvent
} from "react";

import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { timeActions } from "../actions";
import { CaseDot, CasePicker } from "../components/CasePicker";
import {
  caseTone,
  clockLabel,
  clockRange,
  dayBlocks,
  dayGaps,
  gridRange,
  hourOf,
  isFree,
  isoAt,
  type DayBlock
} from "../model";
import type { SignalRow, TimeEntry } from "../types";
import {
  formatDuration,
  formatHours,
  isUnreleased,
  totalHours
} from "../utils";
import { EntryPanel } from "./EntryPanel";

const HPX = 60;
const LENGTHS = [0.25, 0.5, 0.75, 1];

type Props = {
  date: string;
  today: string;
  entries: TimeEntry[];
  signals: SignalRow[];
  focusId?: string | null;
  /** Start a new entry in the latest free slot before now. */
  openNew?: boolean;
  onOpenedNew?: () => void;
};

const nowHours = () => {
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60;
};

const Block: FC<{
  block: DayBlock;
  top: number;
  height: number;
  selected: boolean;
  onSelect: () => void;
}> = ({ block, top, height, selected, onSelect }) => {
  const { entry } = block;
  const tone = caseTone(entry.individual_id);
  const done = !isUnreleased(entry.status);
  const lines = Math.floor((height - 28) / 16);
  return (
    <div
      data-testid="day-block"
      onMouseMove={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={cn(
        "absolute right-0 left-0 box-border flex cursor-pointer flex-col gap-0.5 overflow-hidden rounded-lg border-l-(length:--stripe-width) px-2.5 transition-opacity",
        tone.block,
        tone.text,
        height > 34 ? "justify-start py-1.5" : "justify-center",
        selected && "ring-1 ring-current ring-offset-1",
        done && "opacity-55"
      )}
      style={{ top, height, borderLeftColor: "currentColor" }}
    >
      <span className={cn("flex min-w-0 items-center gap-1.5", tone.text)}>
        {done ? <Check className="size-3" /> : null}
        <span className="truncate text-sm font-medium">
          {entry.case_label || "Needs a case"}
        </span>
        <span className="flex-1" />
        <span className="text-sm font-medium tabular-nums">
          {formatHours(entry.duration_hours)}
        </span>
      </span>
      {lines > 0 && entry.narrative ? (
        <span
          className={cn(
            "overflow-hidden text-sm leading-4 opacity-85",
            tone.text
          )}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: lines,
            WebkitBoxOrient: "vertical"
          }}
        >
          {entry.narrative}
        </span>
      ) : null}
    </div>
  );
};

export const DayView: FC<Props> = ({
  date,
  today,
  entries,
  signals,
  focusId,
  openNew = false,
  onOpenedNew
}) => {
  const { blocks, untimed } = useMemo(() => dayBlocks(entries), [entries]);
  const range = useMemo(() => gridRange(blocks, signals), [blocks, signals]);
  const nowH = date === today ? nowHours() : null;
  const [selId, setSelId] = useState<string | null>(
    focusId ??
      blocks.find((b) => isUnreleased(b.entry.status))?.entry.id ??
      null
  );
  const [hover, setHover] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ start: number; len: number } | null>(
    null
  );
  const draftRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (focusId) setSelId(focusId);
  }, [focusId]);

  useEffect(() => {
    if (!openNew) return;
    const end = nowH !== null ? Math.floor(nowH * 4) / 4 : range.to;
    const slot = [0.5, 0.25]
      .map((len) => {
        for (let h = end - len; h >= range.from; h -= 0.25)
          if (isFree(blocks, h, h + len)) return { start: h, len };
        return null;
      })
      .find(Boolean);
    if (slot) {
      setDraft(slot);
      setSelId(null);
    }
    onOpenedNew?.();
  }, [openNew]);

  useEffect(() => {
    draftRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [draft?.start]);

  const y = (h: number) => (h - range.from) * HPX;
  const selected = entries.find((e) => e.id === selId) ?? null;
  const limit = nowH ?? range.to;
  const gaps = dayGaps(blocks, limit);

  const slotAt = (ev: MouseEvent<HTMLDivElement>): number | null => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const h = Math.floor(((ev.clientY - rect.top) / HPX + range.from) * 4) / 4;
    if (h < range.from || h > range.to - 0.25) return null;
    if (nowH !== null && h >= nowH) return null;
    return isFree(blocks, h, h + 0.25) ? h : null;
  };

  const live = entries.filter((e) => e.status !== "discarded");
  const byCase = Object.values(
    live.reduce<
      Record<string, { id: string | null; label: string; hours: number }>
    >((acc, e) => {
      const key = e.individual_id ?? "none";
      const row = acc[key] ?? {
        id: e.individual_id ?? null,
        label: e.case_label || "Needs a case",
        hours: 0
      };
      row.hours = Math.round((row.hours + e.duration_hours) * 100) / 100;
      acc[key] = row;
      return acc;
    }, {})
  ).sort((a, b) => b.hours - a.hours);
  const maxCase = Math.max(0.1, ...byCase.map((r) => r.hours));

  const addAt = async (individualId: string) => {
    if (!draft) return;
    const created = await timeActions.create({
      work_date: date,
      hours: draft.len,
      individual_id: individualId,
      started_at: isoAt(date, draft.start),
      ended_at: isoAt(date, draft.start + draft.len),
      narrative: ""
    });
    setDraft(null);
    if (created) setSelId(created.id);
  };

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div
        className="min-h-0 overflow-y-auto pt-4 pr-6 pb-6"
        onClick={() => {
          setSelId(null);
          setDraft(null);
        }}
      >
        <div
          className="relative"
          style={{ height: y(range.to) + 8 }}
          data-testid="day-grid"
        >
          {Array.from(
            { length: range.to - range.from + 1 },
            (_, i) => range.from + i
          ).map((h) => (
            <div
              key={h}
              className="pointer-events-none absolute right-0 left-0"
              style={{ top: y(h) }}
            >
              <span className="absolute -top-1.75 left-0 w-13.5 text-right text-sm text-fg-3 tabular-nums">
                {clockLabel(h)}
              </span>
              <span className="absolute top-0 right-0 left-16.5 border-t border-line-2" />
            </div>
          ))}

          <div
            className="absolute top-0 bottom-0 left-16.5 w-1.5"
            data-testid="activity-ribbon"
          >
            {signals.map((s) => {
              const start = hourOf(s.started_at);
              if (start === null) return null;
              const end = Math.max(
                hourOf(s.ended_at) ?? start + 0.1,
                start + 0.05
              );
              const agent = s.kind === "agent" || !s.billable;
              const owner = entries.find((e) =>
                e.activity?.some((a) => a.id === s.id)
              );
              return (
                <span
                  key={s.id}
                  title={`${s.title} · ${clockLabel(start)}${
                    agent ? " · context only" : ""
                  }`}
                  className={cn(
                    "absolute right-0 left-0 box-border cursor-pointer rounded-xs transition-opacity",
                    agent
                      ? "border border-dashed border-line-4 bg-transparent"
                      : caseTone(s.individual_id).dot,
                    owner && owner.id === selId ? "opacity-100" : "opacity-35"
                  )}
                  style={{
                    top: y(start) + 1,
                    height: Math.max(4, y(end) - y(start) - 2)
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (owner) setSelId(owner.id);
                    setDraft(null);
                  }}
                />
              );
            })}
          </div>

          <div
            className={cn(
              "absolute top-0 right-0 bottom-2 left-20.5",
              hover !== null && "cursor-pointer"
            )}
            onMouseMove={(e) => setHover(slotAt(e))}
            onMouseLeave={() => setHover(null)}
            onClick={(e) => {
              e.stopPropagation();
              const h = slotAt(e);
              if (h === null) return;
              const len =
                LENGTHS.slice()
                  .reverse()
                  .find(
                    (l) =>
                      isFree(blocks, h, h + l) &&
                      (nowH === null || h + l <= nowH + 0.01)
                  ) ?? 0.25;
              setDraft({ start: h, len: Math.min(len, 0.5) });
              setSelId(null);
              setHover(null);
            }}
          >
            {blocks.length === 0 ? (
              <span
                className="pointer-events-none absolute right-0 left-0 flex justify-center text-sm text-fg-3"
                style={{ top: y(Math.min(10, range.to - 1)) }}
              >
                Nothing captured on this day. Click any time to add an entry.
              </span>
            ) : null}
            {gaps.map((g) => (
              <span
                key={g.from}
                className="pointer-events-none absolute right-0 left-0 flex items-center justify-center text-sm text-fg-3"
                style={{ top: y(g.from), height: y(g.to) - y(g.from) }}
              >
                {formatDuration((g.to - g.from) * 3600)} with no activity
              </span>
            ))}

            {hover !== null && !draft ? (
              <div
                className="pointer-events-none absolute right-0 left-0 box-border flex items-center gap-1.5 rounded-lg border border-dashed border-line-2 bg-surface px-2.5 text-sm text-fg-3"
                style={{ top: y(hover) + 1, height: HPX / 4 - 2 }}
              >
                <Plus className="size-3" /> Add entry at {clockLabel(hover)}
              </div>
            ) : null}

            {draft ? (
              <div
                ref={draftRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 left-0 box-border flex items-center rounded-lg border border-dashed border-primary bg-primary/5 px-2.5 text-sm font-medium text-primary"
                style={{
                  top: y(draft.start) + 1,
                  height: Math.max(
                    22,
                    y(draft.start + draft.len) - y(draft.start) - 2
                  )
                }}
              >
                New entry · {clockRange(draft.start, draft.start + draft.len)}
              </div>
            ) : null}

            {blocks.map((b) => (
              <Block
                key={b.entry.id}
                block={b}
                top={y(b.start) + 1}
                height={Math.max(22, y(b.end) - y(b.start) - 2)}
                selected={b.entry.id === selId}
                onSelect={() => {
                  setSelId(b.entry.id);
                  setDraft(null);
                }}
              />
            ))}
          </div>

          {nowH !== null && nowH >= range.from && nowH <= range.to ? (
            <div
              className="pointer-events-none absolute right-0 left-0"
              style={{ top: y(nowH) }}
            >
              <span className="absolute -top-1.75 left-0 w-13.5 text-right text-sm font-medium text-danger tabular-nums">
                {clockLabel(Math.floor(nowH * 12) / 12)}
              </span>
              <span className="absolute -top-0.75 left-15.75 size-1.5 rounded-full bg-danger" />
              <span className="absolute top-0 right-0 left-16.5 border-t border-danger" />
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-col border-l border-line-2">
        {draft ? (
          <div
            className="flex flex-1 flex-col gap-4 overflow-y-auto p-5"
            data-testid="day-draft"
          >
            <span className="flex flex-col gap-0.5">
              <span className="text-md font-medium">New entry</span>
              <span className="text-sm text-fg-3 tabular-nums">
                {clockRange(draft.start, draft.start + draft.len)} · nothing
                captured here
              </span>
            </span>
            <span className="flex flex-col gap-2">
              <span className="text-sm font-medium text-fg-3">Length</span>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={String(draft.len)}
                onValueChange={(v) =>
                  v && setDraft({ ...draft, len: Number(v) })
                }
              >
                {LENGTHS.map((l) => (
                  <ToggleGroupItem
                    key={l}
                    value={String(l)}
                    disabled={!isFree(blocks, draft.start, draft.start + l)}
                  >
                    {formatDuration(l * 3600)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </span>
            <span className="flex flex-col gap-2">
              <span className="text-sm font-medium text-fg-3">Case</span>
              <CasePicker
                onPick={(c) => void addAt(c.individual_id)}
                className="w-full"
              />
            </span>
            <span>
              <Button variant="ghost" size="xs" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </span>
          </div>
        ) : selected ? (
          <EntryPanel entry={selected} onDone={() => setSelId(null)} />
        ) : (
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <span className="flex flex-col gap-0.5">
              <span className="text-md font-medium">
                {nowH !== null ? "Your day so far" : "This day"}
              </span>
              <span className="text-sm text-fg-3 tabular-nums">
                {formatHours(totalHours(live))} across{" "}
                {byCase.filter((r) => r.id).length}{" "}
                {byCase.filter((r) => r.id).length === 1 ? "case" : "cases"}
              </span>
            </span>
            <div className="flex flex-col gap-3">
              {byCase.map((r) => (
                <span key={r.id ?? "none"} className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <CaseDot id={r.id} />
                    <span className="truncate text-sm">{r.label}</span>
                    <span className="flex-1" />
                    <span className="text-sm font-medium tabular-nums">
                      {formatHours(r.hours)}
                    </span>
                  </span>
                  <span className="h-1 overflow-hidden rounded-full bg-fill-2">
                    <span
                      className={cn("block h-full", caseTone(r.id).dot)}
                      style={{ width: `${(r.hours / maxCase) * 100}%` }}
                    />
                  </span>
                </span>
              ))}
            </div>
            {untimed.length ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-fg-3">
                  Without a time of day
                </span>
                {untimed.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelId(e.id)}
                    className="flex items-center gap-2 rounded-md border border-line-2 px-2 py-1.5 text-left hover:bg-fill-2"
                  >
                    <CaseDot id={e.individual_id} />
                    <span className="flex-1 truncate text-sm">
                      {e.narrative || e.case_label || "Needs a case"}
                    </span>
                    <span className="text-sm font-medium tabular-nums">
                      {formatHours(e.duration_hours)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            <span className="flex gap-2 text-sm leading-4.5 text-fg-3">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              Select an entry to review it. To log a call or offline work, click
              any empty time on the left.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
